// Silhouette sizing + shipping constants (#228 follow-up). The silhouette is sold
// as TWO price tiers (small / large) but the buyer picks an EXACT size on a
// continuous slider in the preview; these constants + pure helpers turn the pet's
// printed aspect and the shipping boxes into that slider's cm range and the S/L
// split. Kept in @pufimi/shared as the single seam these product values live
// behind — the operator now edits the box dimensions in the admin Settings panel
// (`silhouette_max_long_cm` / `_max_short_cm` / `_small_long_cm` / `_small_short_cm`),
// and these constants are only the DEFAULTS the settings fall back to.

// The LARGE shipping box the finished pillow must fit inside, `[longer, shorter]`
// cm — the DEFAULT for the `silhouette_max_long_cm` / `_max_short_cm` settings. The
// pillow is scaled to CONTAIN within this box at the pet's own aspect (the longest
// side is bounded by the longer axis, the shorter side by the shorter). Orientation
// is irrelevant — the pillow's LONGER side is compared with the larger value and its
// SHORTER side with the smaller, whichever way the photo is turned (Leon, 2026-07-12).
export const SHIPPING_BOX_CM: readonly [number, number] = [70, 40];

// The SMALL shipping box `[longer, shorter]` cm — the DEFAULT for the
// `silhouette_small_long_cm` / `_small_short_cm` settings. A pillow that FITS inside
// it (its longer side ≤ this longer axis AND its shorter side ≤ this shorter axis)
// ships in the cheaper small box and bills as the SMALL tier; anything that spills
// past either axis bills as LARGE. This is the physical S/L split Leon asked for
// (2026-07-12): "the threshold below which I can use smaller boxes."
export const SILHOUETTE_SMALL_BOX_CM: readonly [number, number] = [50, 35];

// The smallest offered silhouette is this fraction of the box-fit maximum
// (longest side). 0.5 ⇒ the slider spans `[0.5·max, max]`. Independent of the box
// dimensions so "how small can it get" is its own product lever (Leon, #228).
export const SILHOUETTE_SMALL_FRACTION = 0.5;

// The large + small shipping boxes, each `[longer, shorter]` cm. The preview passes
// the operator's edited values (from tier-pricing); everything else falls back to
// the constant defaults.
export interface SilhouetteBoxesCm {
  big: readonly [number, number];
  small: readonly [number, number];
}

export const SILHOUETTE_DEFAULT_BOXES: SilhouetteBoxesCm = {
  big: SHIPPING_BOX_CM,
  small: SILHOUETTE_SMALL_BOX_CM,
};

const clamp01 = (t: number): number => Math.min(1, Math.max(0, t));

// The slider's longest-side cm range for a pet of the given face aspect, contained
// within the shipping box.
export interface SilhouetteRangeCm {
  minCm: number;
  maxCm: number;
}

// The largest longest-side cm that still fits a pet of ratio `longOverShort`
// (longest face side ÷ shortest, ≥ 1) inside the box: the longest side is capped
// by the box's longer axis, and the shorter side (= long / ratio) by the box's
// shorter axis — whichever binds first. For a 2:1 pet in a 70×40 box that's
// `min(70, 40·2) = 70` (a 70×35 pillow); for a square, `min(70, 40) = 40`.
export function silhouetteMaxLongCm(
  longOverShort: number,
  box: readonly [number, number] = SHIPPING_BOX_CM,
): number {
  const [boxLong, boxShort] = box;
  const ratio = Math.max(1, longOverShort);
  return Math.min(boxLong, boxShort * ratio);
}

export function silhouetteRangeCm(
  longOverShort: number,
  box: readonly [number, number] = SHIPPING_BOX_CM,
): SilhouetteRangeCm {
  const maxCm = silhouetteMaxLongCm(longOverShort, box);
  return { minCm: SILHOUETTE_SMALL_FRACTION * maxCm, maxCm };
}

// Slider position t∈[0,1] → the longest-side cm within the range.
export function silhouetteLongCmForT(t: number, range: SilhouetteRangeCm): number {
  return range.minCm + (range.maxCm - range.minCm) * clamp01(t);
}

export type SilhouetteTier = "S" | "L";

// The tier a pillow whose LONGEST side is `longCm` bills as, for a pet of aspect
// `longOverShort`: SMALL iff the whole pillow fits inside the small box (its longer
// side ≤ the box's longer axis AND its shorter side ≤ the shorter axis — exactly
// `silhouetteMaxLongCm` for the small box). Anything larger is LARGE. Rotation-free:
// the fit compares longer-vs-longer and shorter-vs-shorter, not width-vs-height.
export function silhouetteTierForLongCm(
  longCm: number,
  longOverShort: number,
  smallBox: readonly [number, number] = SILHOUETTE_SMALL_BOX_CM,
): SilhouetteTier {
  return longCm <= silhouetteMaxLongCm(longOverShort, smallBox) ? "S" : "L";
}

// The tier a pillow of an EXACT footprint (W × H cm) bills as — the server-authoritative
// entry the cart's cm slider commits against (ADR-0056 / #1). The buyer picks precise cm on
// the slider; the price tier follows from whether that footprint fits the SMALL box, derived
// here from the cm ALONE (longest side + the pet's own aspect) — never from a client-sent
// tier. Rotation-free (longer-vs-longer, shorter-vs-shorter), like `silhouetteTierForLongCm`.
export function silhouetteTierForDimsCm(
  dims: { width: number; height: number },
  smallBox: readonly [number, number] = SILHOUETTE_SMALL_BOX_CM,
): SilhouetteTier {
  const longCm = Math.max(dims.width, dims.height);
  const shortCm = Math.min(dims.width, dims.height);
  const longOverShort = shortCm > 0 ? longCm / shortCm : 1;
  return silhouetteTierForLongCm(longCm, longOverShort, smallBox);
}

// The slider-space position (0..1) of the S/L threshold line: where the pillow's
// longest side crosses the largest longest-side that still fits the SMALL box. UNLIKE
// the old fraction split this is PHOTO-DEPENDENT (Leon, 2026-07-12) — a physical cm
// threshold lands at a different slider spot for each pet aspect. Clamps to 1 when
// even the biggest pillow fits the small box (all small ⇒ line at the far end) and
// to 0 when even the smallest spills it (all large ⇒ line at the start).
export function silhouetteMarkerT(
  longOverShort: number,
  boxes: SilhouetteBoxesCm = SILHOUETTE_DEFAULT_BOXES,
): number {
  const { minCm, maxCm } = silhouetteRangeCm(longOverShort, boxes.big);
  const span = maxCm - minCm;
  if (span <= 0) return 1;
  const smallFitLong = silhouetteMaxLongCm(longOverShort, boxes.small);
  return clamp01((smallFitLong - minCm) / span);
}

// The tier a slider position bills as, for a given pet aspect + boxes. Resolves the
// slider position to its longest-side cm, then compares against the small box —
// exact at the "everything fits" / "nothing fits" edges (where the marker clamps).
export function silhouetteTierForT(
  t: number,
  longOverShort: number,
  boxes: SilhouetteBoxesCm = SILHOUETTE_DEFAULT_BOXES,
): SilhouetteTier {
  const range = silhouetteRangeCm(longOverShort, boxes.big);
  const longCm = silhouetteLongCmForT(t, range);
  return silhouetteTierForLongCm(longCm, longOverShort, boxes.small);
}

// The slider's default starting position — mid-range. Kept at the historical 0.52 so
// the 3D scale anchor (`silhouetteScale`, which divides by `longSideFraction` at this
// t) renders the default at the cushion's natural mesh size (scale 1). The S/L
// threshold is now a physical per-photo line (`silhouetteMarkerT`), so the default
// can no longer be "a hair past the marker" as a photo-independent constant — the
// slider simply opens mid-range and the tier there is whatever the boxes make it.
export const SILHOUETTE_DEFAULT_T = 0.52;
